import React, { useState, useEffect } from 'react';
import { Bell, Settings, User, Search, LogOut, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { KaiHeaderButton } from '@saas-foundation/shared-frontend/kai';
import { clearAuthSession } from '@saas-foundation/shared-frontend/utils/sessionKeepAlive';

/**
 * Shared Header Component
 * Search bar plus three action icons: Notifications, Settings, and User menu (Profile, Settings, Logout).
 *
 * @param {string} title - Application title
 * @param {string} searchPlaceholder - Placeholder text for search bar
 * @param {string} userName - User name to display (falls back to localStorage user or "User")
 * @param {function} onSearch - Optional callback when search is submitted (query string)
 * @param {function} onLogout - Optional custom logout; default clears token and redirects to /login
 * @param {number} notificationCount - Optional unread count to show badge on Bell
 */
const Header = ({
  title = "End-to-End RCM",
  searchPlaceholder = "Search...",
  userName: userNameProp,
  onSearch,
  onLogout,
  notificationCount = 0,
}) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [userName, setUserName] = useState(userNameProp || 'User');

  useEffect(() => {
    if (userNameProp) {
      setUserName(userNameProp);
      return;
    }
    try {
      const stored = localStorage.getItem('user');
      if (stored) {
        const user = JSON.parse(stored);
        const name = user.first_name && user.last_name
          ? `${user.first_name} ${user.last_name}`
          : user.email || 'User';
        setUserName(name);
      }
    } catch (_) {
      // keep default
    }
  }, [userNameProp]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (onSearch) {
      onSearch(searchQuery);
    } else {
      // Default: could navigate to a search page or do nothing
      if (searchQuery.trim()) {
        navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      }
    }
  };

  const handleNotifications = () => {
    navigate('/notifications');
  };

  const handleSettings = () => {
    navigate('/settings');
    setShowUserMenu(false);
  };

  const handleProfile = () => {
    navigate('/profile');
    setShowUserMenu(false);
  };

  const handleLogout = () => {
    setShowUserMenu(false);
    if (onLogout) {
      onLogout();
      return;
    }
    clearAuthSession();
    window.location.href = '/login';
  };

  const getUserInitials = () => {
    try {
      const stored = localStorage.getItem('user');
      if (stored) {
        const user = JSON.parse(stored);
        if (user.first_name && user.last_name) {
          return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
        }
        if (user.email) return user.email[0].toUpperCase();
      }
    } catch (_) {}
    return userName.charAt(0).toUpperCase();
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo and Title */}
          <div className="flex items-center flex-shrink-0">
            <h1 className="text-xl font-bold text-gray-900 text-left truncate">
              {title}
            </h1>
          </div>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="flex-1 max-w-lg mx-4 md:mx-8 min-w-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                aria-label="Search"
              />
            </div>
          </form>

          {/* Right side: Kai, Notifications, Settings, User menu */}
          <div className="flex items-center space-x-2 md:space-x-3 flex-shrink-0">
            <KaiHeaderButton />

            {/* Notifications */}
            <button
              type="button"
              onClick={handleNotifications}
              className="relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Notifications"
              aria-label="Notifications"
            >
              <Bell className="w-5 h-5" />
              {notificationCount > 0 && (
                <span className="absolute top-0.5 right-0.5 bg-red-500 text-white text-xs font-bold rounded-full min-w-[1.25rem] h-5 flex items-center justify-center px-1 border-2 border-white">
                  {notificationCount > 9 ? '9+' : notificationCount}
                </span>
              )}
            </button>

            {/* Settings */}
            <button
              type="button"
              onClick={handleSettings}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Settings"
              aria-label="Settings"
            >
              <Settings className="w-5 h-5" />
            </button>

            {/* User menu */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-2 p-1.5 md:p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                aria-expanded={showUserMenu}
                aria-haspopup="true"
                aria-label="User menu"
              >
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                  {getUserInitials()}
                </div>
                <span className="text-sm font-medium text-gray-700 hidden sm:block max-w-[120px] truncate">
                  {userName}
                </span>
                <ChevronDown className="w-4 h-4 text-gray-400 hidden sm:block" />
              </button>

              {showUserMenu && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    aria-hidden="true"
                    onClick={() => setShowUserMenu(false)}
                  />
                  <div
                    className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50"
                    role="menu"
                  >
                    <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
                      <p className="text-sm font-semibold text-gray-900 truncate">{userName}</p>
                    </div>
                    <button
                      type="button"
                      onClick={handleProfile}
                      className="w-full flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                      role="menuitem"
                    >
                      <User className="w-4 h-4 mr-3 text-gray-400 flex-shrink-0" />
                      Profile
                    </button>
                    <button
                      type="button"
                      onClick={handleSettings}
                      className="w-full flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                      role="menuitem"
                    >
                      <Settings className="w-4 h-4 mr-3 text-gray-400 flex-shrink-0" />
                      Settings
                    </button>
                    <div className="border-t border-gray-200 my-1" />
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="w-full flex items-center px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 cursor-pointer rounded-b-lg"
                      role="menuitem"
                    >
                      <LogOut className="w-4 h-4 mr-3 flex-shrink-0" />
                      Logout
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;

